import { useMemo } from 'react';
import type { RaceRecording } from '../../shared/race';
import type { SessionView } from '../../shared/session-view';
import { projectSessionChartBase, revealSessionChart, type SessionChartProjection } from '../../shared/session-chart';

export interface SessionChartInput {
  recording?: RaceRecording;
  sessionView: SessionView;
  presentation?: 'results' | 'live' | 'replay';
  followingLive?: boolean;
  liveHeadAtMs?: number;
}

/**
 * The chart read model shared by every visual mode. Fact retention belongs to
 * the session runtime; this hook only projects those facts into a stable
 * ChartModel and reveals the prefix allowed by the current display cursor.
 */
export type SessionChart = SessionChartProjection;

export function useSessionChart({ recording, sessionView, presentation = 'live', followingLive = false, liveHeadAtMs }: SessionChartInput): SessionChart {
  const snapshot = sessionView.snapshot;
  const visualHead = followingLive ? liveHeadAtMs ?? snapshot.atMs : snapshot.atMs;
  // Fact references are the cache boundary. A same-length correction creates
  // a new events array and therefore rebuilds the chart index; cursor motion
  // only reveals the cached frontier, while a newly crossed chart fact
  // projects that prefix once. RAF ticks never rescan the full event stream.
  const base = useMemo(() => projectSessionChartBase(recording, snapshot), [
    recording?.events,
    recording?.drivers,
    recording?.track,
    recording?.coverage,
    recording?.session,
    recording?.durationMs,
    snapshot.session.key,
    recording ? undefined : snapshot.atMs,
    recording ? undefined : snapshot.drivers,
  ]);
  return useMemo(() => revealSessionChart(base, snapshot, presentation, visualHead), [
    base,
    snapshot.atMs,
    snapshot.lap,
    presentation,
    visualHead,
  ]);
}
